import { isAppStateResponse, isEmptyResponse, isIncidentResponse } from "core/api/api.typesGuards";
import type { CommandResponseTypes } from "core/api/apiHelpers";
import type * as I from "core/api/coreApiClient";
import { CommandApi, LogApi, SessionApi } from "core/api/coreApiClient";
import { openAPIWrapper } from "core/api/openApi";

export async function getStatic(sessionId?: string) {
  return openAPIWrapper(SessionApi)(async (api) => {
    const response = await api.getStatic(undefined, sessionId);

    if (isAppStateResponse(response)) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export async function getInit() {
  return openAPIWrapper(SessionApi)(async (api) => {
    const response = await api.getInit();

    if (isAppStateResponse(response)) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export async function logout() {
  return openAPIWrapper(SessionApi)(async (api) => {
    await api.logout();

    return Promise.resolve();
  });
}

export async function login(userName: string, password: string) {
  const credentials: I.Credentials = { userName, password };

  return openAPIWrapper(SessionApi)(async (api) => {
    const response = await api.login({ credentials });

    if (isAppStateResponse(response)) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export type LogTarget = "ERROR" | "DEBUG";

export async function logError(log: I.LogEntry, target: LogTarget = "ERROR") {
  return openAPIWrapper(LogApi)(async (api) => {
    const response =
      target === "ERROR"
        ? await api.logError({
            logEntry: log,
          })
        : await api.logDebug({
            logEntry: log,
          });

    if (isIncidentResponse(response)) {
      return response.payload;
    }
    if (isEmptyResponse(response)) {
      return undefined;
    }

    return Promise.reject();
  });
}

export async function commandGet(path: string, retry?: boolean) {
  return openAPIWrapper(CommandApi, {
    pathOverride: path,
    // Retries up to 3 times to fetch a command for unhandled errors only (caught in retryMiddleware)
    retry,
  })(async (api) => {
    const response = await api.get();

    return Promise.resolve(response);
  });
}

export async function commandSave(path: string, payload: unknown) {
  return openAPIWrapper(CommandApi, { pathOverride: path })(async (api) => {
    const response = (await api.save({ body: payload as object })) as CommandResponseTypes;

    return Promise.resolve(response);
  });
}

export async function commandUpdate(path: string, payload?: unknown) {
  return openAPIWrapper(CommandApi, { pathOverride: path })(async (api) => {
    const response = (await api.update({ body: payload as object })) as CommandResponseTypes;

    return Promise.resolve(response);
  });
}

export async function commandDelete(path: string) {
  return openAPIWrapper(CommandApi, { pathOverride: path })(async (api) => {
    const response = (await api._delete()) as CommandResponseTypes;

    return Promise.resolve(response);
  });
}

export async function autocomplete(path: string, payload?: unknown) {
  return openAPIWrapper(CommandApi, { pathOverride: path })(async (api) => {
    const response = (await api.update({ body: payload as object })) as I.AutoCompletionResultResponse;

    return Promise.resolve(response);
  });
}

export async function quickSearch(payload: I.EntitySearchQuickQuery) {
  return openAPIWrapper(CommandApi, { pathOverride: "/entitySearch/quick" })(async (api) => {
    const response = (await api.update({ body: payload as object })) as I.EntitySearchQuickResultResponse;

    return Promise.resolve(response);
  });
}

export async function extendedSearch(payload: I.EntitySearchExtendedQuery) {
  return openAPIWrapper(CommandApi, { pathOverride: "/entitySearch/extended" })(async (api) => {
    const response = (await api.update({ body: payload as object })) as I.EntitySearchExtendedResultResponse;

    return Promise.resolve(response);
  });
}
